// ✅ src/pages/OrderManagement/ProductionInwardPrint.jsx  (FULL FILE)
// Route (your app): /OrderManagement/Tar_Inward_Print?ProdInID=123
//
// ✅ Data source priority:
// 1) navigate state (state.row) from listing page (fast, no API call)
// 2) API call by ProdInID (refresh-safe)
//    GET  https://nissi-india.com/api/inwardListing/view/:prodInId
//    -> { success:true, data:{ ... } }
// 3) sessionStorage fallback (ProdInID)
//
// NOTE: Your table columns:
// tbl_Tar_ProductionInwardMaster:
// fld_ProdInID, fld_ProjectName, fld_ProductionName, fld_ProductionCode, fld_Inwardcode,
// fld_Date, AvailableQty, RecievedQty, fld_workTypeName, fld_isapproved, etc.

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/inwardListing";

// dd/MM/yyyy formatter
const fmtDMY = (val) => {
  if (!val) return "";
  // if already dd/MM/yyyy, keep it
  const s = String(val);
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;

  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return s;

  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  return `${dd}/${mm}/${yy}`;
};

// helper get first non-empty
const v = (obj, ...keys) => {
  for (const k of keys) {
    const val = obj?.[k];
    if (val !== undefined && val !== null && String(val).trim() !== "") return val;
  }
  return "";
};

export default function ProductionInwardPrint() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();

  // ✅ ProdInID from: state -> query -> sessionStorage
  const prodInId = useMemo(() => {
    return (
      location.state?.ProdInID ||
      location.state?.prodInId ||
      location.state?.row?.fld_ProdInID ||
      location.state?.row?.ProdInID ||
      params.get("ProdInID") ||
      params.get("prodInId") ||
      sessionStorage.getItem("ProdInID") ||
      ""
    );
  }, [location.state, params]);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);

  const fetchPrintData = async (id) => {
    try {
      setErr("");
      setLoading(true);

      if (!id) {
        setErr("ProdInID missing in URL");
        setData(null);
        return;
      }

      // ✅ refresh-safe API call
      const res = await axios.get(`${API_BASE}/view/${encodeURIComponent(id)}`);

      const ok = res.data?.success !== false;
      const row = res.data?.data || null;

      if (!ok || !row) {
        setErr(res.data?.message || "No data found");
        setData(null);
        return;
      }

      setData(row);
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to load print data");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // ✅ First try to use row passed from listing page (FAST)
    const passedRow = location.state?.row || null;

    if (passedRow && Object.keys(passedRow).length > 0) {
      setData(passedRow);
      setErr("");
      // still store for refresh fallback
      if (prodInId) sessionStorage.setItem("ProdInID", String(prodInId));
      return;
    }

    // ✅ otherwise fetch by id
    if (prodInId) {
      sessionStorage.setItem("ProdInID", String(prodInId));
      fetchPrintData(prodInId);
    } else {
      setErr("ProdInID missing");
      setData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prodInId]);

  // ✅ map fields based on your real table
  const inwardNo = v(data, "InwardCode", "fld_Inwardcode", "fld_InwardCode", "inwardCode");
  const inwardDate = v(data, "Date", "fld_Date", "inwardDate");
  const projectName = v(data, "ProjectName", "fld_ProjectName", "fld_projectname");
  const productionName = v(data, "ProductionName", "fld_ProductionName", "productionname");
  const productionCode = v(data, "ProductionCode", "fld_ProductionCode", "productionCode");
  const workTypeName = v(data, "WorkTypeName", "fld_workTypeName", "workTypeName");

  const availableQty = v(data, "AvailableQty", "availableQty");
  const receivedQty = v(data, "RecievedQty", "Recievedqty", "ReceivedQty", "receivedQty");

  // Signatures: your table doesn’t have user names; keep blank unless API joins users
  const preparedBy = v(data, "PreparedBy", "preparedBy", "CreatedByName");
  const checkedBy = v(data, "CheckedBy", "checkedBy");
  const approvedBy = v(data, "ApprovedBy", "approvedBy");

  return (
    <div style={{ background: "#e5e7eb", minHeight: "100vh", padding: 16 }}>
      {/* ✅ Print styles */}
      <style>{`
        @page { size: A4; margin: 10mm; }
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
          .page-wrap { background: white !important; padding: 0 !important; }
          .sheet { box-shadow: none !important; border: 0 !important; }
        }
      `}</style>

      {/* Top controls */}
      <div className="no-print" style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #d1d5db",
            background: "white",
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          Back
        </button>

        <button
          onClick={() => window.print()}
          disabled={loading}
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #111827",
            background: "#111827",
            color: "white",
            cursor: "pointer",
            fontWeight: 700,
            opacity: loading ? 0.6 : 1,
          }}
        >
          Print
        </button>
      </div>

      <div className="page-wrap" style={{ display: "flex", justifyContent: "center" }}>
        {/* A4-ish sheet */}
        <div
          className="sheet"
          style={{
            width: 794, // ~A4 @ 96dpi
            minHeight: 1123,
            background: "white",
            border: "2px solid #111",
            boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
            position: "relative",
          }}
        >
          {/* inner border */}
          <div style={{ margin: 14, border: "1px solid #111", minHeight: 1123 - 28, position: "relative" }}>
            {/* Header with logo + company */}
            <div style={{ display: "grid", gridTemplateColumns: "110px 1fr", gap: 10, padding: 12 }}>
              {/* Logo box */}
              <div style={{ border: "1px solid #111", height: 72, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ fontSize: 10, fontWeight: 700, textAlign: "center" }}>
                  LOGO
                  <div style={{ fontWeight: 600, fontSize: 8 }}>NISSI INFOTECH</div>
                </div>
              </div>

              {/* Company details */}
              <div style={{ border: "1px solid #111", padding: "8px 10px", textAlign: "center" }}>
                <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 2 }}>Nissi Infotech</div>
                <div style={{ fontSize: 12, marginBottom: 2 }}>Nissi</div>
                <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 4 }}>CIVIL CONSTRUCTION</div>
                <div style={{ fontSize: 11 }}>
                  3/42-12, Nagappa colony, kavundampalayam--641030, Tamil Nadu.
                </div>
                <div style={{ fontSize: 11 }}>
                  Phone No : 9003744644 &nbsp;&nbsp; Email : infraconsoft-implementation@nissiinfotech.in
                </div>
              </div>
            </div>

            {/* Title */}
            <div style={{ borderTop: "1px solid #111", borderBottom: "1px solid #111", padding: "10px 12px", textAlign: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 800 }}>Production Inward Report</div>
            </div>

            {/* Loading/Error */}
            {(loading || err) && (
              <div style={{ padding: 12 }}>
                {loading && <div style={{ fontWeight: 700 }}>Loading...</div>}
                {err && <div style={{ color: "#b91c1c", fontWeight: 700 }}>{err}</div>}
              </div>
            )}

            {/* Body fields */}
            <div style={{ padding: "16px 18px" }}>
              {/* Row 1 */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 30, marginBottom: 22 }}>
                <div style={{ fontSize: 12 }}>
                  <div style={{ fontWeight: 800 }}>Inward No:</div>
                  <div style={{ marginTop: 6, minHeight: 16 }}>{String(inwardNo || "")}</div>
                </div>
                <div style={{ fontSize: 12 }}>
                  <div style={{ fontWeight: 800 }}>Date:</div>
                  <div style={{ marginTop: 6, minHeight: 16 }}>{fmtDMY(inwardDate)}</div>
                </div>
              </div>

              {/* Row 2 */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 30, marginBottom: 22 }}>
                <div style={{ fontSize: 12 }}>
                  <div style={{ fontWeight: 800 }}>Project Name:</div>
                  <div style={{ marginTop: 6, minHeight: 16 }}>{String(projectName || "")}</div>
                </div>
                <div style={{ fontSize: 12 }}>
                  <div style={{ fontWeight: 800 }}>Production Name:</div>
                  <div style={{ marginTop: 6, minHeight: 16 }}>{String(productionName || "")}</div>
                </div>
              </div>

              {/* Row 3 */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 30, marginBottom: 22 }}>
                <div style={{ fontSize: 12 }}>
                  <div style={{ fontWeight: 800 }}>Production Code:</div>
                  <div style={{ marginTop: 6, minHeight: 16 }}>{String(productionCode || "")}</div>
                </div>
                <div style={{ fontSize: 12 }}>
                  <div style={{ fontWeight: 800 }}>Work Type Name:</div>
                  <div style={{ marginTop: 6, minHeight: 16 }}>{String(workTypeName || "")}</div>
                </div>
              </div>

              {/* Row 4 */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 30, marginBottom: 16 }}>
                <div style={{ fontSize: 12 }}>
                  <div style={{ fontWeight: 800 }}>Available Qty:</div>
                  <div style={{ marginTop: 6, minHeight: 16 }}>{String(availableQty || "")}</div>
                </div>
                <div style={{ fontSize: 12 }}>
                  <div style={{ fontWeight: 800 }}>Recieved Qty:</div>
                  <div style={{ marginTop: 6, minHeight: 16 }}>{String(receivedQty || "")}</div>
                </div>
              </div>
            </div>

            {/* Divider line */}
            <div style={{ borderTop: "1px solid #111" }} />

            {/* Signature area */}
            <div style={{ padding: "22px 18px", minHeight: 520 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginTop: 40 }}>
                <div style={{ fontSize: 12, textAlign: "left" }}>
                  <div style={{ fontWeight: 800, marginBottom: 10 }}>Prepared By</div>
                  <div style={{ minHeight: 18 }}>{String(preparedBy || "")}</div>
                </div>
                <div style={{ fontSize: 12, textAlign: "center" }}>
                  <div style={{ fontWeight: 800, marginBottom: 10 }}>Checked By</div>
                  <div style={{ minHeight: 18 }}>{String(checkedBy || "")}</div>
                </div>
                <div style={{ fontSize: 12, textAlign: "right" }}>
                  <div style={{ fontWeight: 800, marginBottom: 10 }}>Approved By</div>
                  <div style={{ minHeight: 18 }}>{String(approvedBy || "")}</div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div
              style={{
                position: "absolute",
                left: 12,
                right: 12,
                bottom: 12,
                border: "1px solid #111",
                padding: "8px 10px",
                fontSize: 10,
              }}
            >
              Report generated by ERP-Software - www.infraconsoft.com owned by www.nissiinfotech.com
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
